import { differenceInCalendarDays, isToday } from 'date-fns'
import {
  DropdownDivider,
  DropdownItem,
  Dropdown,
  Icon,
} from 'semantic-ui-react'
import * as React from 'react'

import { Collection, RecipeSchedule, Task } from '../../types'
import CardMenuItemRecipe from './CardMenuItemRecipe'
import CardMenuItemTask from './CardMenuItemTask'
import featureToggles from '../../featureToggles'
import { classNames } from '../../utils'
import usePowers from '../../hooks/usePowers'

const normalizeItems = (tasks: Task[], schedules: RecipeSchedule[]) => {
  const normalizedTasks = tasks.map((task) => ({
    ...task,
    type: 'task' as const,
    sortDate: task.dueDate,
    originalData: task,
  }))

  const normalizedSchedules = schedules.map((schedule) => ({
    ...schedule,
    type: 'schedule' as const,
    sortDate: schedule.whenToRun || 0,
    originalData: schedule,
  }))

  return [...normalizedTasks, ...normalizedSchedules].sort(
    (a, b) => a.sortDate - b.sortDate,
  )
}

interface Props {
  onCreateScheduleClick(): void
  onUpdateScheduleClick(schedule: RecipeSchedule): void
  onDeleteScheduleClick(schedule: RecipeSchedule): void
  onCompleteTaskClick(task: Task): void
  onUpdateTaskClick(task: Task): void
  onCreateTaskClick(): void
  relatedCollection: Collection
  schedules: RecipeSchedule[]
  pointing: 'left' | 'right'
  tasks: Task[]

  onRemoveContactAndCompanyClick?: () => void
}

export default function CardMenu(props: Props) {
  const {
    onCreateScheduleClick,
    onUpdateScheduleClick,
    onDeleteScheduleClick,
    onCompleteTaskClick,
    onUpdateTaskClick,
    onCreateTaskClick,
    onRemoveContactAndCompanyClick,
    relatedCollection,
    schedules,
    pointing,
    tasks,
  } = props

  const powers = usePowers()

  const unDoneTasks = tasks.filter((task) => !task.isDone)

  const anythingToday =
    unDoneTasks.some((task) => isToday(task.dueDate)) ||
    schedules.some((schedule) => isToday(schedule.whenToRun || 0))

  const hasOverdueTasks = unDoneTasks.some((task) => {
    return differenceInCalendarDays(task.dueDate, Date.now()) < 0
  })

  const mergedItems = React.useMemo(
    () => normalizeItems(tasks, schedules),
    [tasks, schedules],
  )

  return (
    <>
      <Dropdown
        icon={null}
        pointing={pointing}
        trigger={
          <button
            className={classNames(
              '-m-1.5 block rounded-full p-1.5 text-[17px] leading-none hover:text-slate-700',
              unDoneTasks.length === 0 && schedules.length === 0
                ? 'text-yellow-300'
                : hasOverdueTasks
                  ? 'text-red-600'
                  : anythingToday
                    ? 'text-primary'
                    : 'text-slate-500',
            )}
            type="button"
          >
            <Icon
              name={
                unDoneTasks.length === 0 && schedules.length === 0
                  ? 'exclamation circle'
                  : 'chevron circle right'
              }
              className="!mr-0"
            />
          </button>
        }
      >
        <Dropdown.Menu
          className={classNames(
            '[.dropdown.upward_&]:translate-y-3 [.dropdown:not(.upward)_&]:-translate-y-3',
            mergedItems.length > 0 && '!w-[300px] !min-w-0',
          )}
        >
          {mergedItems.map((item) =>
            item.type === 'task' ? (
              <CardMenuItemTask
                onCompleteTaskClick={onCompleteTaskClick}
                onUpdateTaskClick={onUpdateTaskClick}
                task={item.originalData}
                key={`task-${item._id}`}
              />
            ) : (
              <CardMenuItemRecipe
                onDeleteScheduleClick={(schedule) => {
                  onDeleteScheduleClick(schedule)
                }}
                onUpdateScheduleClick={(schedule) => {
                  onUpdateScheduleClick(schedule)
                }}
                relatedCollection={relatedCollection}
                schedule={item.originalData}
                key={`recipe-${item._id}`}
              />
            ),
          )}

          {mergedItems.length > 0 && <DropdownDivider />}

          <DropdownItem
            text="Schedule a Task"
            icon="check circle outline"
            onClick={onCreateTaskClick}
          />
          {powers.hasAll(['run-and-schedule-recipes']) &&
            featureToggles.recipes && (
              <DropdownItem
                text="Schedule a Recipe"
                icon="lab"
                onClick={onCreateScheduleClick}
              />
            )}

          {relatedCollection === 'contacts' && (
            <>
              <DropdownDivider></DropdownDivider>

              <DropdownItem
                style={{ color: 'var(--red)' }}
                text={`Remove Contact & Company`}
                icon="trash"
                onClick={onRemoveContactAndCompanyClick}
              />
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </>
  )
}
